import { Upload } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import { evalsService } from '../services/evalsService';
import { isActiveRun, type EvalModel, type EvalRunItem, type EvalSuiteInfo, type PromptOverride } from '../types/evalTypes';
import { COST_NOTE, formatUsd, runCost, sumCosts, sumSplits } from '../utils/evalCost';
import { formatDateTime, formatDuration, formatPassRate, plural } from '../utils/evalFormat';
import { rate, shades } from '../utils/evalShades';
import { ArmLabel } from './ArmLabel';
import { CostSplitText } from './CostSplitText';
import { DraftPromptModal } from './DraftPromptModal';
import { RunStatusBadge } from './RunStatusBadge';

interface RunListProps {
  runs: EvalRunItem[];
  models: EvalModel[];
  /** For the production badge. */
  suites: EvalSuiteInfo[];
}

interface ViewingPrompt {
  armId: string;
  prompt?: PromptOverride;
  loading: boolean;
  error: string | null;
}

/**
 * One arm in the Arms and Passed columns. The fixed height lines up each arm with its rate,
 * and `w-max` keeps the label on one line, so the table never squeezes it into two.
 */
const ARM_LINE = 'flex h-7 w-max items-center';

const ReviewBadge =({ review }: Pick<EvalRunItem, 'review'>) => {
  if (!review) {
    return (
      <span className="text-gray-300" title="Not reviewed">
        –
      </span>
    );
  }
  return review.decision === 'go' ? (
    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Go</span>
  ) : (
    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">No-go</span>
  );
};

export const RunList = ({ runs, models, suites }: RunListProps) => {
  const navigate = useNavigate();
  const [viewing, setViewing] = useState<ViewingPrompt | null>(null);

  // The list has no prompt texts; the run detail has them, so fetch it on demand.
  const showDraft = async (runId: string, armId: string) => {
    setViewing({ armId, loading: true, error: null });
    try {
      const { prompts } = await evalsService.getRun(runId);
      setViewing({ armId, prompt: prompts.find((prompt) => prompt.armId === armId), loading: false, error: null });
    } catch (caught) {
      setViewing({ armId, loading: false, error: caught instanceof Error ? caught.message : 'Failed to load the draft prompt.' });
    }
  };

  const total = sumSplits(runs.map((run) => runCost(models, run)));

  if (runs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <EmptyState title="No eval runs yet" description="Runs you start appear here once they finish." />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500">
          <tr>
            <th className="font-medium px-4 py-2">Started</th>
            <th className="font-medium px-4 py-2">Suite</th>
            <th className="font-medium px-4 py-2">Arms</th>
            <th className="font-medium px-4 py-2">Passed</th>
            <th className="font-medium px-4 py-2">Trials</th>
            <th className="font-medium px-4 py-2" title={COST_NOTE}>
              Est. cost
            </th>
            <th className="font-medium px-4 py-2">Review</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const productionModel = suites.find((suite) => suite.id === run.suiteId)?.productionModel;
            const summaries = run.arms.map((arm) => run.summary.arms.find((candidate) => candidate.armId === arm.id));
            const rateShades = shades(
              summaries.map((summary) => (summary ? rate(summary.passed, summary.trials) : null)),
              'higher',
            );
            const cost = runCost(models, run);
            const active = isActiveRun(run.status);
            const progress = run.total > 0 ? Math.round((run.resultCount / run.total) * 100) : 0;
            const showStatus =run.status !== 'completed' || run.regradeOf !== undefined || run.tokenName !== undefined;
            return (
              <tr
                key={run.runId}
                onClick={() => navigate(`/evals/${run.runId}`)}
                className="border-t border-gray-100 align-top cursor-pointer hover:bg-gray-50"
              >
                <td className="px-4 py-2 whitespace-nowrap">
                  <Link to={`/evals/${run.runId}`} className="block leading-7 text-blue-600 hover:underline" onClick={(event) => event.stopPropagation()}>
                    {formatDateTime(run.startedAt)}
                  </Link>
                  <span className="block text-xs text-gray-400">{formatDuration(run.durationMs)}</span>
                  {showStatus && (
                    <span className="block mt-1">
                      <RunStatusBadge run={run} hideCompleted />
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs leading-7 whitespace-nowrap">{run.suiteId}</td>
                <td className="px-4 py-2">
                  {run.arms.map((arm) => (
                    <div key={arm.id} className={ARM_LINE}>
                      <ArmLabel arm={arm} models={models} productionModel={productionModel} compact onShowDraft={() => void showDraft(run.runId, arm.id)} />
                    </div>
                  ))}
                </td>
                <td className="px-4 py-2">
                  {run.arms.map((arm, index) => {
                    const summary = summaries[index];
                    return (
                      <div key={arm.id} className={ARM_LINE}>
                        <span className={`px-1.5 py-0.5 rounded tabular-nums ${rateShades[index] ?? 'text-gray-700'}`}>
                          {summary ? formatPassRate(summary.passed, summary.trials) : '–'}
                        </span>
                        {summary && summary.refusals > 0 && <span className="ml-1.5 text-xs text-red-600">{summary.refusals} refused</span>}
                      </div>
                    );
                  })}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {active ? (
                    <>
                      <span className="block leading-7 tabular-nums">
                        {run.resultCount} <span className="text-gray-400">/ {run.total}</span>
                      </span>
                      <span
                        className="block h-1 w-24 mb-1 bg-gray-100 rounded-full overflow-hidden"
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <span
                          className={`block h-full rounded-full transition-[width] duration-700 ${run.cancelRequested ? 'bg-amber-400' : 'bg-blue-500'}`}
                          style={{ width: `${progress}%` }}
                        />
                      </span>
                    </>
                  ) : (
                    <span className="block leading-7">{run.resultCount}</span>
                  )}
                  <span className="block text-xs text-gray-400">
                    {plural(run.caseIds.length, 'case')} × {run.trials}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap" title={COST_NOTE}>
                  <span className="block leading-7">
                    <span className="text-xs text-gray-500">Model</span> {formatUsd(cost.model)}
                  </span>
                  <span className="block text-xs text-gray-400">Judge {formatUsd(cost.judge)}</span>
                </td>
                <td className="px-4 py-2">
                  <span className="flex min-h-7 flex-wrap items-center gap-1">
                    <ReviewBadge review={run.review} />
                    {run.pendingSuggestions > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap bg-violet-100 text-violet-800">
                        {plural(run.pendingSuggestions, 'suggestion')}
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Upload size={14} />}
                  title="Load this run's setup into the new run form"
                  aria-label="Load this run's setup into the new run form"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/evals?load=${run.runId}`);
                  }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-200 bg-gray-50 align-top" title={COST_NOTE}>
            <td colSpan={5} className="px-4 py-2 text-sm font-medium text-gray-700">
              Total spent, {plural(runs.length, 'run')} shown
            </td>
            <td colSpan={3} className="px-4 py-2">
              <span className="block text-sm font-medium text-gray-900">{formatUsd(sumCosts([total.model, total.judge]))}</span>
              <CostSplitText cost={total} />
            </td>
          </tr>
        </tfoot>
      </table>

      <DraftPromptModal
        armId={viewing?.armId ?? null}
        prompt={viewing?.prompt}
        loading={viewing?.loading}
        error={viewing?.error}
        onClose={() => setViewing(null)}
      />
    </div>
  );
};
